import { Button } from "@component/button";
import { AlertTriangleIcon, InfoIcon } from "lucide-react";
import { Checkbox } from "./checkbox";
import { Label } from "./label";
import { useCallback, useState } from "react";

interface Props {
    isOpen: boolean;
    isTestMode?: boolean;
    isEarlyExit?: boolean;
    remainingMinutes: number;
    onClose: () => void;
    onConfirm: (isDontShowAgain: boolean) => void;
    /**
     * Use "roleplay" for the training product to swap interview-flavoured
     * copy. Defaults to "interview".
     */
    sessionMode?: "interview" | "roleplay";
}

export function InterviewEndConfirmDialogV2(props: Props) {
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const isLessThanAMinute = props.remainingMinutes <= 0;
    const minuteLabel = props.remainingMinutes === 1 ? 'minute' : 'minutes';
    const isRoleplay = props.sessionMode === "roleplay";
    const sessionNoun = isRoleplay ? "roleplay" : "interview";

    const handleOnConfirm = useCallback(() => {
        props.onConfirm(isChecked)
    }, [isChecked])

    return (
        <div className="fixed inset-0 bg-[rgba(17,24,39,0.55)] flex items-center justify-center z-50 p-4">
            <div className="rounded-lg shadow-lg max-w-md w-full p-6 relative bg-white">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex-shrink-0 bg-yellow-100 rounded-full flex items-center justify-center">
                        <AlertTriangleIcon className="h-5 w-5 text-yellow-700" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-base font-semibold text-gray-900">
                        You're almost there!
                    </h2>
                </div>
                <div className="mb-6">
                    {props.isEarlyExit ? (
                        <>
                            <p className="mb-1.5 text-sm text-gray-600 leading-relaxed">
                                {isLessThanAMinute ? (
                                    <>Just <b className="text-gray-900">less than a minute</b> left to complete your full {sessionNoun}.</>
                                ) : (
                                    <>Just <b className="text-gray-900">{props.remainingMinutes} more {minuteLabel}</b> to complete your full {sessionNoun}.</>
                                )}
                            </p>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                {isRoleplay
                                    ? "Finishing gives you the most complete coaching feedback on your performance."
                                    : "Finishing gives the hiring team a more complete picture of your strengths beyond your CV."}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Just <b className="text-gray-900">{props.remainingMinutes || 15} more minutes</b> to {isRoleplay ? "finish your roleplay." : "submit your interview and proceed to next steps."}
                            <br /><br />
                            {isRoleplay
                                ? "Completing a bit more gives you the most complete coaching feedback."
                                : "Completing a bit more helps the hiring team better understand your skills beyond your CV."}
                        </p>
                    )}
                </div>
                {/* Alert Info */}
                {props.isTestMode && (
                    <div className="mb-6">
                        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
                            <InfoIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                {isRoleplay
                                    ? "This appears when an employee tries to end the roleplay before completing it. It encourages them to continue so they get the full coaching benefit."
                                    : "This appears when a candidate tries to end an AI interview before completing it. It encourages them to continue so the hiring team receives enough information for a meaningful review."}
                            </p>
                        </div>
                    </div>
                )}
                {props.isTestMode && (
                    <div className="flex items-start space-x-3 mb-5">
                        <Checkbox
                            id="dont-show-again-interview"
                            onCheckedChange={(checked) => setIsChecked(checked as boolean)}
                            className={`mt-0.5 ${isChecked ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-border"}`}
                        />
                        <Label
                            htmlFor="dont-show-again-interview"
                            className="text-sm text-foreground cursor-pointer"
                        >
                            Don't show this again
                        </Label>
                    </div>
                )}
                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleOnConfirm}
                        className="flex-1 h-[44px]"
                    >
                        {isRoleplay ? "End Roleplay" : "End Interview"}
                    </Button>
                    <Button
                        type="button"
                        onClick={props.onClose}
                        className="flex-1 h-[44px]"
                    >
                        {isRoleplay ? "Continue Roleplay" : "Continue Interview"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
